import { ChevronLeftIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { DisabledTooltip } from '@/components/DisabledTooltip';
import { SecondaryContent } from '@/components/NavSidebar/NavLinks';
import { WorkspacePicker } from '@/components/NavSidebar/WorkspacePicker';
import { Button, Text } from '@/design-system';
import { useAuth, useUserSSOInfo } from '@/hooks/useAuth/useAuth';
import { useCurrentTier, useOrgRequiresPayment } from '@/hooks/useCurrentTier';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import {
  useCurrentOrganization,
  useOrganizationId,
  useTenantList,
} from '@/hooks/useSwr';
import { appOrganizationPath, mixedLoginMethods } from '@/utils/constants';
import { isSelfHosted } from '@/utils/is-self-hosted';
import { cn } from '@/utils/tailwind';

import { OrgPicker } from './components/OrgPicker';
import { OrgSettingsTabLink } from './components/OrgSettingsTabLink';
import { UpsellBadge } from './components/UpsellBadge';
import { WorkspacesNavItem } from './components/WorkspacesNavItem';

type Props = {
  className?: string;
};

type SettingsTab = 'workspace' | 'members' | 'usage' | 'payments';

const OrganizationSettingsSideNav = (props: Props) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const organizationId = useOrganizationId();

  const org = useCurrentOrganization();
  const { isSSOLogin } = useUserSSOInfo();
  const tenants = useTenantList({ skip: !auth.authed });

  const { isEnterprise, isNoPlan } = useCurrentTier();
  const isPersonal = org.data?.is_personal;

  const { requiresPayment } = useOrgRequiresPayment();
  const paymentEnabledFF = useInstanceFlagOrDefault(
    InstanceFlags.payment_enabled
  ) as boolean;

  // Disable back button for SAML users with no tenants (empty state)
  const isSAMLEmptyState =
    isSSOLogin && (!tenants.data || tenants.data.length === 0);
  const disableBackButton =
    (isNoPlan && !isSelfHosted && paymentEnabledFF) || isSAMLEmptyState;

  const canInviteMembers =
    mixedLoginMethods.basic || (!isNoPlan && !isPersonal);

  const tabEligibility: Record<SettingsTab, boolean> = useMemo(() => {
    return {
      workspace:
        auth.needsAuth && !requiresPayment && (!isNoPlan || isSelfHosted),
      members: auth.needsAuth,
      apikeys: auth.needsAuth,
      oauthproviders: auth.needsAuth,
      models: !requiresPayment && isPersonal,
      shared: auth.needsAuth && isPersonal,
      secrets: !requiresPayment && isPersonal,
      feedbacks: !requiresPayment && isPersonal,
      rules: !requiresPayment && isPersonal,
      usage: !requiresPayment && isEnterprise,
      payments: paymentEnabledFF && !isSelfHosted && !isEnterprise,
    };
  }, [
    auth.needsAuth,
    requiresPayment,
    paymentEnabledFF,
    isEnterprise,
    isPersonal,
    isNoPlan,
    isSelfHosted,
  ]);

  let content = (
    <>
      <DisabledTooltip
        disabled={disableBackButton}
        disabledMsg={
          isSAMLEmptyState ? (
            <>
              <div>No workspaces available</div>
              <div>Contact your administrator</div>
            </>
          ) : (
            <>
              <div>Subscribe to access all features</div>
              <div>or select another organization</div>
            </>
          )
        }
      >
        <Link to={`/${appOrganizationPath}/${organizationId}/`}>
          <Button
            variant="plain"
            color="secondary"
            disabled={disableBackButton}
            leftDecorator={ChevronLeftIcon}
          >
            Back
          </Button>
        </Link>
      </DisabledTooltip>

      <div className="mt-8 flex flex-col whitespace-nowrap">
        {auth.needsAuth && <OrgPicker className="mb-4" />}
        {tabEligibility['workspace'] && <WorkspacesNavItem />}
        {tabEligibility['members'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/members`}
            disabled={!canInviteMembers && paymentEnabledFF}
          >
            {!canInviteMembers && paymentEnabledFF ? (
              <div className="flex flex-1 items-center justify-between px-2">
                <Text>Members and roles</Text>

                <UpsellBadge
                  tooltipMsg={
                    'Subscribe to Plus to invite members to your team'
                  }
                  onClick={() => {
                    navigate(
                      `/${appOrganizationPath}/${organizationId}/settings/payments`
                    );
                  }}
                  tier="PLUS"
                />
              </div>
            ) : (
              'Members and roles'
            )}
          </OrgSettingsTabLink>
        )}
        {tabEligibility['apikeys'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/apikeys`}
          >
            API Keys
          </OrgSettingsTabLink>
        )}
        {tabEligibility['oauthproviders'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/oauth-providers`}
          >
            OAuth Providers
          </OrgSettingsTabLink>
        )}
        {tabEligibility['models'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/models`}
          >
            Models
          </OrgSettingsTabLink>
        )}
        {tabEligibility['shared'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/shared`}
          >
            Shared
          </OrgSettingsTabLink>
        )}
        {tabEligibility['secrets'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/secrets`}
          >
            Secrets
          </OrgSettingsTabLink>
        )}
        {tabEligibility['feedbacks'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/feedbacks`}
          >
            Feedback tags
          </OrgSettingsTabLink>
        )}

        {tabEligibility['usage'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/usage`}
          >
            Usage configuration
          </OrgSettingsTabLink>
        )}
        {tabEligibility['payments'] && (
          <OrgSettingsTabLink
            to={`/${appOrganizationPath}/${organizationId}/settings/payments`}
          >
            Billing and usage
          </OrgSettingsTabLink>
        )}
      </div>
    </>
  );

  if (!organizationId) {
    content = <LinearProgress />;
  }
  return (
    <header
      className={cn(
        'fixed bottom-0 left-0 top-0 flex flex-col items-stretch gap-6 border-r border-r-secondary pb-4 pt-6 transition-all duration-200 ease-in-out',
        'text-xs'
      )}
      style={{
        width: '235px',
        paddingLeft: '0',
        paddingRight: '0',
      }}
      {...props}
    >
      <div
        className={
          'flex flex-1 flex-col justify-between px-[6px] transition-all duration-200 ease-in-out'
        }
      >
        <div className="flex flex-col gap-1">{content}</div>

        <div className="flex flex-col gap-1">
          <SecondaryContent expanded={true} />
          <WorkspacePicker expanded={true} />
        </div>
      </div>
    </header>
  );
};

export default OrganizationSettingsSideNav;
